import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ExternalLink, ListTodo, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useFlagState, useFlagStore } from '../../contexts/FlagStateContext';
import { flagTypeById, makeTaskFromFlag, patchFlag, shortDate } from '../../lib/flags';
import { FlagIcon, useFixedPosition } from './FlagIcon';
import { DismissBackdrop } from '../DismissBackdrop';

/**
 * One flag (#316): its line, where it lives, and - for a type that can be
 * addressed - Mark addressed with an optional reply. Opened from the margin
 * (`where="document"`, which may also change its type or remove it, both
 * document edits) or from a list (`where="list"`, which offers Open in
 * context instead). Addressing only PATCHes the flag; the body is untouched.
 */

export interface FlagPopoverProps {
  flagId: string;
  /** The type from the document or list, used until the state arrives. */
  type: string;
  rect: DOMRect;
  where: 'document' | 'list';
  text: string;
  sourceLine?: string;
  personName?: string | null;
  onChangeType?: (rect: DOMRect) => void;
  onRemove?: () => void;
  onOpenInContext?: () => void;
  onOpenTask?: (taskId: string) => void;
  onClose: () => void;
}

export function FlagPopover(props: FlagPopoverProps) {
  const { flagId, rect, where, text, sourceLine, personName, onChangeType, onRemove, onOpenInContext, onOpenTask, onClose } = props;
  const store = useFlagStore();
  const st = useFlagState(flagId);
  const def = flagTypeById(store.types(), st?.type ?? props.type);
  const toast = useToast();
  const [reply, setReply] = useState(st?.reply ?? '');
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pos = useFixedPosition(ref, rect.left, rect.bottom + 6, rect.top);
  const ready = !!st && !busy;
  const done = st?.status === 'addressed';

  // A reply edited elsewhere (another tab, undo) shows up here.
  const seenReply = useRef(st?.reply);
  useEffect(() => {
    if (st?.reply !== seenReply.current) { seenReply.current = st?.reply; setReply(st?.reply ?? ''); }
  }, [st?.reply]);

  useEffect(() => {
    const away = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const esc = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const send = async (body: { status?: 'open' | 'addressed'; reply?: string | null }, message: string) => {
    setBusy(true);
    try {
      store.setLocal(await patchFlag(flagId, body));
      toast.success(message);
    } catch {
      toast.error('Could not save the flag. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const replyValue = () => reply.trim() || null;
  const address = () => send({ status: 'addressed', reply: replyValue() }, `${def.label} addressed`);
  const save = () => send({ reply: replyValue() }, 'Saved');
  const reopen = () => send({ status: 'open' }, 'Reopened');

  const makeTask = async () => {
    if (st?.task_id) { onOpenTask?.(st.task_id); return; }
    setBusy(true);
    try {
      const r = await makeTaskFromFlag(flagId);
      if (st) store.setLocal({ ...st, task_id: r.task_id });
      toast.success(r.created ? 'Task created' : 'Already linked to a task');
    } catch {
      toast.error('Could not make the task. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const heading = `${def.label}${personName ? ` ${personName}` : ''}`;
  const ghost = 'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary disabled:opacity-50';

  return createPortal(
    <>
    {/* #121: a tap outside only closes the popover - it does not also land on what is underneath. */}
    <DismissBackdrop zClass="z-[39]" onDismiss={onClose} />
    <div ref={ref} role="dialog" aria-label={heading}
      className="flag-popover fixed z-40 flex w-[min(350px,calc(100vw-16px))] flex-col gap-2.5 rounded-xl border border-border-hover bg-bg-surface p-3.5 shadow-2xl"
      style={{ left: pos.left, top: pos.top }}>
      <div className="flex items-center gap-2 font-semibold" style={{ color: def.color }}>
        <FlagIcon def={def} size={17} />
        <span className="flex-1 truncate">{heading}</span>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-text-muted hover:bg-bg-hover hover:text-text-primary">
          <X size={15} aria-hidden />
        </button>
      </div>
      <div className="rounded-lg border border-border bg-bg-card px-2.5 py-2 text-sm text-text-primary break-words whitespace-pre-wrap">
        {text || <i className="text-text-muted">Empty line</i>}
      </div>
      {sourceLine && <p className="m-0 text-xs text-text-muted">{sourceLine}</p>}

      {!st && <p className="m-0 text-xs text-text-muted" role="status">Saving...</p>}

      {def.addressable ? (
        <>
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className={`inline-flex items-center gap-1.5 font-semibold ${done ? 'text-success' : 'text-warning'}`}>
              <span className="h-[7px] w-[7px] rounded-full bg-current" />
              {done ? `Addressed${st?.addressed_at ? ` ${shortDate(st.addressed_at)}` : ''}` : 'Open'}
            </span>
            {st?.task_id && <span className="rounded-full border border-border bg-bg-card px-2 text-text-secondary">Linked to a task</span>}
          </div>
          <label htmlFor={`flag-reply-${flagId}`} className="flex items-center justify-between text-xs text-text-secondary">
            <span>{def.replyLabel} (optional)</span>
            <kbd className="font-mono text-[10px] text-text-muted border border-border-hover rounded px-1">Ctrl+Enter</kbd>
          </label>
          <textarea id={`flag-reply-${flagId}`} value={reply} disabled={!ready} maxLength={2000}
            placeholder={def.placeholder ?? 'What happened?'} onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); if (ready) void (done ? save() : address()); } }}
            className="min-h-[66px] w-full resize-y rounded-md border border-border-hover bg-bg-input px-2 py-1.5 text-sm text-text-primary" />
          <div className="flex flex-wrap gap-1.5">
            {done ? (
              <>
                <button type="button" disabled={!ready} onClick={save} className="rounded-md bg-accent px-3 py-1 text-sm text-white hover:bg-accent-hover disabled:opacity-50">Save</button>
                <button type="button" disabled={!ready} onClick={reopen} className="rounded-md border border-border-hover px-3 py-1 text-sm text-text-primary hover:bg-bg-hover disabled:opacity-50">Reopen</button>
              </>
            ) : (
              <button type="button" disabled={!ready} onClick={address} className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1 text-sm text-white hover:bg-accent-hover disabled:opacity-50">
                <Check size={14} aria-hidden />Mark addressed
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="m-0 text-xs text-text-muted">A decision is a record, so there is nothing to address. It still appears in Find.</p>
      )}

      <div className="h-px bg-border" />
      <div className="flex flex-wrap gap-1">
        <button type="button" disabled={!ready} onClick={makeTask} className={ghost}><ListTodo size={14} aria-hidden />{st?.task_id ? 'Open task' : 'Make task'}</button>
        {where === 'document' ? (
          <>
            {onChangeType && <button type="button" onClick={(e) => onChangeType(e.currentTarget.getBoundingClientRect())} className={ghost}>Change type</button>}
            {onRemove && <button type="button" onClick={onRemove} className={`${ghost} hover:text-danger`}><X size={14} aria-hidden />Remove</button>}
          </>
        ) : (
          onOpenInContext && <button type="button" onClick={onOpenInContext} className={ghost}><ExternalLink size={14} aria-hidden />Open in context</button>
        )}
      </div>
    </div>
    </>,
    document.body,
  );
}
